import { computed } from 'vue';
import { useRouter } from 'vue-router';

import { useFlowGuard } from '@/composables/useFlowGuard';
import { usePageController } from '@/modules/common/state/usePageController';

import { provideBlacklistEditorContext } from '../../../../composables/context';
import { useBlacklistDirtyGuard } from '../../../../composables/useBlacklistDirtyGuard';
import { useRuleParentGroup } from '../../../../composables/useRuleParentGroup';
import { useBlacklistRuleDelete } from '../actions/useBlacklistRuleDelete';
import { useBlacklistRuleSave } from '../actions/useBlacklistRuleSave';
import { useBlacklistEditorState } from '../state/useBlacklistEditorState';
import type { BlacklistEditorContext } from './types';

/**
 * Page-level orchestrator for the rule editor. Called by `BlacklistAutoRuleEditorLayout`, not by
 * the routed page: the shell header (Save / Discard) lives in the layout and must read the same
 * instance as the page body. Both editor routes share that layout, so `/:id` → `/new` reuses it —
 * the state composable reseeds for that case. Returns the context as well as providing it, so a
 * test host can drive it without a child component to inject into.
 */
export const useBlacklistEditorContextProvider = (): BlacklistEditorContext => {
  const router = useRouter();
  const page = usePageController();
  const state = useBlacklistEditorState();
  const parentGroup = useRuleParentGroup(state.id);
  // A rule opened from its group page returns there; the group row id is the route's `groupId`.
  const back = () =>
    router.push(
      parentGroup.value
        ? { name: 'blacklist-rule-group-details', params: { groupId: parentGroup.value.id } }
        : { name: 'blacklist-auto-rules-poc' },
    );
  const { isCancelled } = useFlowGuard();
  const save = useBlacklistRuleSave({ page, state, back, isCancelled });
  const { remove, removePending } = useBlacklistRuleDelete({ state, isCancelled });
  const isPending = save.state.isPending;
  const canSubmit = computed(
    () => state.ready.value && state.isDirty.value && !isPending.value && !removePending.value,
  );

  const discard = (): void => {
    state.restoreSeed();
    state.conditionError.value = '';
    state.nameError.value = '';
  };

  useBlacklistDirtyGuard(state.isDirty);

  const retry = async (): Promise<void> => {
    await Promise.all([
      state.queries.dictionaries.refetch(),
      ...(state.id.value === null ? [] : [state.queries.rule.refetch()]),
    ]);
  };

  const context: BlacklistEditorContext = {
    id: state.id,
    title: state.title,
    parentGroup,
    page,
    form: state.form,
    fields: state.fields,
    conditions: state.conditions,
    conditionError: state.conditionError,
    nameError: state.nameError,
    loading: state.loading,
    loadError: state.loadError,
    loadErrorMessage: state.loadErrorMessage,
    ready: state.ready,
    isDirty: state.isDirty,
    isPending,
    canSubmit,
    save,
    removePending,
    setBanAdUnitTypes: state.setBanAdUnitTypes,
    discard,
    remove,
    back,
    retry,
  };

  provideBlacklistEditorContext(context);
  return context;
};
